/**
 * World pictures for the world picker, all drawn with hard pixel edges like Minecraft itself
 * (shown with `image-rendering: pixelated`, never smoothed):
 *
 * - a world's own picture is the `icon.png` Minecraft saves in the world folder (64 × 64): the desktop app reads it
 *   from the folder, a recent .zip world from the zip kept in this browser (`controller.recentWorldIcon`);
 * - the demo island gets a top-down map of itself, shaded like Minecraft's map item (`demoIslandPicture`);
 * - a world without a picture of its own gets a small pixel map made from its name (`namePicture`): always the same
 *   for the same name, so a world keeps its look in every list. Nothing here comes from Mojang's textures.
 */

const fromName = new Map<string, string>();

/** 32-bit hash of a string (FNV-1a) */
function hash(s: string) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  return h;
}

/** small fast PRNG (mulberry32) */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type RGB = [number, number, number];
// terrain bands, low to high (colours of the map, not of block textures)
const DEEP: RGB = [44, 82, 168], WATER: RGB = [63, 118, 228], SAND: RGB = [219, 205, 142], GRASS: RGB = [111, 170, 64],
  GRASS_2: RGB = [96, 154, 54], FOREST: RGB = [52, 110, 38], STONE: RGB = [125, 125, 125], SNOW: RGB = [238, 243, 246];

function toDataUrl(px: Uint8ClampedArray<ArrayBuffer>, w: number, h: number) {
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  const ctx = c.getContext("2d");
  if (!ctx) return "";
  ctx.putImageData(new ImageData(px, w, h), 0, 0);
  return c.toDataURL("image/png");
}

/**
 * A 32 × 32 pixel map made from a world's name: smoothed random heights (two octaves) become sea, beach, grass,
 * forest, stone and snow, with Minecraft's map shading (lighter where the land rises from the north).
 */
export function namePicture(name: string): string {
  const cached = fromName.get(name);
  if (cached !== undefined) return cached;
  const N = 32; // pixels
  const r = rng(hash(name.trim().toLowerCase() || "world"));
  /** smooth value noise on a G × G grid, sampled at pixel (x, z) */
  const octave = (G: number) => {
    const grid = Array.from({ length: G * G }, () => r());
    return (x: number, z: number) => {
      const gx = (x / (N - 1)) * (G - 1), gz = (z / (N - 1)) * (G - 1);
      const x0 = Math.floor(gx), z0 = Math.floor(gz), x1 = Math.min(G - 1, x0 + 1), z1 = Math.min(G - 1, z0 + 1);
      const fx = gx - x0, fz = gz - z0;
      const sx = fx * fx * (3 - 2 * fx), sz = fz * fz * (3 - 2 * fz);
      const top = grid[z0 * G + x0] * (1 - sx) + grid[z0 * G + x1] * sx;
      const bottom = grid[z1 * G + x0] * (1 - sx) + grid[z1 * G + x1] * sx;
      return top * (1 - sz) + bottom * sz;
    };
  };
  const broad = octave(4), detail = octave(9);
  // more land than sea, and a coast on one side
  const tilt = r() * Math.PI * 2, bias = 0.06 + r() * 0.1;
  const at = (x: number, z: number) => {
    const coast = (Math.cos(tilt) * (x / N - 0.5) + Math.sin(tilt) * (z / N - 0.5)) * 0.55;
    return broad(x, z) * 0.78 + detail(x, z) * 0.22 + bias - coast;
  };
  const height = new Float32Array(N * N);
  for (let z = 0; z < N; z++) for (let x = 0; x < N; x++) height[z * N + x] = at(x, z);
  const px = new Uint8ClampedArray(N * N * 4);
  for (let z = 0; z < N; z++) {
    for (let x = 0; x < N; x++) {
      const h = height[z * N + x];
      const speck = r();
      const col: RGB = h < 0.3 ? DEEP : h < 0.4 ? WATER : h < 0.45 ? SAND : h < 0.62 ? (speck < 0.06 ? GRASS_2 : GRASS)
        : h < 0.76 ? (speck < 0.12 ? GRASS_2 : FOREST) : h < 0.86 ? STONE : SNOW;
      const north = z > 0 ? height[(z - 1) * N + x] : h;
      const water = h < 0.4;
      const shade = water ? 1 : h > north + 0.012 ? 1 : h < north - 0.012 ? 0.72 : 0.86;
      const i = (z * N + x) * 4;
      px[i] = col[0] * shade;
      px[i + 1] = col[1] * shade;
      px[i + 2] = col[2] * shade;
      px[i + 3] = 255;
    }
  }
  const url = toDataUrl(px, N, N);
  fromName.set(name, url);
  return url;
}

let island: Promise<string | null> | null = null;

/**
 * The demo island seen from above, 64 × 64: one pixel per 16 × 16 blocks (its top block: ground, water or a tree's
 * crown, engine/demo.worker.ts), shaded like Minecraft's map item (lighter where the ground rises from the north,
 * darker where it falls).
 */
export function demoIslandPicture(view: (n: number) => Promise<{ n: number; color: Uint8Array; height: Int16Array }>): Promise<string | null> {
  island ??= view(64).then(({ n: N, color, height }) => {
    const px = new Uint8ClampedArray(N * N * 4);
    for (let z = 0; z < N; z++) {
      for (let x = 0; x < N; x++) {
        const i = z * N + x, up = Math.max(0, z - 1) * N + x;
        const shade = z === 0 || height[i] === height[up] ? 0.86 : height[i] > height[up] ? 1 : 0.72;
        px[i * 4] = color[i * 4] * shade;
        px[i * 4 + 1] = color[i * 4 + 1] * shade;
        px[i * 4 + 2] = color[i * 4 + 2] * shade;
        px[i * 4 + 3] = 255;
      }
    }
    return toDataUrl(px, N, N);
  }).catch(() => {
    island = null;
    return null;
  });
  return island;
}
